import {
  completeAnyWord,
  completeFromList,
  snippetCompletion,
  type Completion,
  type CompletionContext,
  type CompletionResult,
  type CompletionSource,
} from "@codemirror/autocomplete";
import { syntaxTree } from "@codemirror/language";
import { EditorState, type Extension, type TransactionSpec } from "@codemirror/state";
import type { SyntaxNode } from "@lezer/common";
import { abbreviationBefore, expandAbbreviation, expandCss } from "./emmet";

// ───────────── Emmet ─────────────

const HTML_TAGS = new Set(
  (
    "a abbr address article aside audio b blockquote body br button canvas caption code col dd details dialog " +
    "div dl dt em fieldset figcaption figure footer form h1 h2 h3 h4 h5 h6 head header hr html i iframe img " +
    "input label legend li link main mark meta nav noscript ol optgroup option output p picture pre progress " +
    "q section select small source span strong style sub summary sup svg table tbody td template textarea " +
    "tfoot th thead time title tr u ul video btn"
  ).split(" "),
);

const NOT_CONTENT = new Set([
  "OpenTag",
  "CloseTag",
  "SelfClosingTag",
  "Attribute",
  "AttributeValue",
  "Comment",
  "ProcessingInst",
  "DoctypeDecl",
  "JSXOpenTag",
  "JSXCloseTag",
  "JSXSelfClosingTag",
  "JSXAttribute",
  "JSXAttributeValue",
  "String",
  "TemplateString",
  "LineComment",
  "BlockComment",
]);

function ancestors(node: SyntaxNode | null) {
  const out: string[] = [];
  for (let n = node; n; n = n.parent) out.push(n.name);
  return out;
}

/** O cursor está no conteúdo (entre tags), não dentro de uma tag, atributo ou comentário. */
function inMarkupContent(ctx: CompletionContext, jsx: boolean): boolean {
  const names = ancestors(syntaxTree(ctx.state).resolveInner(ctx.pos, -1));
  if (names.some((n) => NOT_CONTENT.has(n))) return false;
  // JSX: só dentro de um elemento (no meio do código JS, "a.b" é propriedade, não Emmet).
  return jsx ? names.includes("JSXElement") || names.includes("JSXFragment") : true;
}

const preview = (snippet: string) => () => {
  const pre = document.createElement("pre");
  pre.className = "cm-emmet-preview";
  pre.textContent = snippet.replace(/\$\{\d+(?::([^}]*))?\}/g, "$1").replace(/\u200b/g, "");
  return pre;
};

/** Emmet em HTML (e JSX): `!`, `ul>li*3`, `.card>h2{Título}+p`… — Enter ou Tab expande. */
export function emmetMarkup(jsx = false): CompletionSource {
  return (ctx): CompletionResult | null => {
    const line = ctx.state.doc.lineAt(ctx.pos);
    const before = line.text.slice(0, ctx.pos - line.from);
    const abbr = abbreviationBefore(before);
    if (!abbr || !inMarkupContent(ctx, jsx)) return null;
    // Palavra solta: só nomes de tag conhecidos (texto comum não vira <palavra>).
    if (/^[a-z][a-z0-9]*$/i.test(abbr) && !HTML_TAGS.has(abbr.toLowerCase())) return null;
    if (jsx && abbr === "!") return null;
    // Recuo com "\t": o snippet do CodeMirror troca cada \t pela unidade de recuo do arquivo e
    // alinha as linhas novas pela linha atual.
    const text = expandAbbreviation(abbr, { jsx, indent: "\t" });
    if (!text) return null;
    return {
      from: ctx.pos - abbr.length,
      options: [
        snippetCompletion(text, {
          label: abbr,
          detail: abbr === "!" ? "documento HTML5" : "Emmet",
          type: "keyword",
          boost: 99,
          info: preview(text),
        }),
      ],
      filter: false,
    };
  };
}

/** Emmet de CSS no começo de uma declaração: `m10`, `p10-20`, `w100p`, `df`, `jcc`… */
export const emmetCss: CompletionSource = (ctx) => {
  const line = ctx.state.doc.lineAt(ctx.pos);
  const before = line.text.slice(0, ctx.pos - line.from);
  const m = /[a-z][a-z0-9.-]*$/.exec(before);
  if (!m) return null;
  const pre = before.slice(0, before.length - m[0].length).trimEnd();
  if (pre && !/[{;]$/.test(pre)) return null;
  const names = ancestors(syntaxTree(ctx.state).resolveInner(ctx.pos, -1));
  if (!names.includes("Block")) return null;
  const expanded = expandCss(m[0]);
  if (!expanded) return null;
  return {
    from: ctx.pos - m[0].length,
    options: [
      snippetCompletion(expanded, {
        label: m[0],
        detail: expanded.replace(/\$\{\d+(?::([^}]*))?\}/g, "$1"),
        type: "property",
        boost: 60,
      }),
    ],
    filter: false,
  };
};

// ───────────── Snippets ─────────────

const s = (label: string, template: string, detail: string): Completion =>
  snippetCompletion(template, { label, detail, type: "keyword" });

/** Atalhos que o VS Code e as extensões populares trazem (console.log, componente React…). */
export const jsSnippets: Completion[] = [
  s("log", "console.log(${});", "console.log"),
  s("clg", "console.log(${});", "console.log"),
  s("cle", "console.error(${});", "console.error"),
  s("afn", "(${params}) => {\n\t${}\n}", "função de seta"),
  s("asyncfn", "async function ${nome}(${params}) {\n\t${}\n}", "função assíncrona"),
  s("tryc", "try {\n\t${}\n} catch (error) {\n\tconsole.error(error);\n}", "try/catch"),
  s("imp", 'import ${nome} from "${módulo}";', "import"),
  s("imd", 'import { ${nome} } from "${módulo}";', "import nomeado"),
  s("exp", "export default ${nome};", "export default"),
  s("fetchj", 'const res = await fetch("${url}");\nconst data = await res.json();', "fetch JSON"),
  s("timeout", "setTimeout(() => {\n\t${}\n}, ${1000});", "setTimeout"),
  s(
    "rfc",
    "export default function ${Componente}() {\n\treturn (\n\t\t<div>\n\t\t\t${}\n\t\t</div>\n\t);\n}",
    "componente React",
  ),
  s("us", "const [${valor}, set${Valor}] = useState(${inicial});", "useState"),
  s("ue", "useEffect(() => {\n\t${}\n}, [${deps}]);", "useEffect"),
  s("um", "const ${valor} = useMemo(() => ${}, [${deps}]);", "useMemo"),
  s("ur", "const ${ref} = useRef(${null});", "useRef"),
];

export const pythonSnippets: Completion[] = [
  s("def", "def ${nome}(${params}):\n\t${pass}", "função"),
  s("class", "class ${Nome}:\n\tdef __init__(self, ${params}):\n\t\t${pass}", "classe"),
  s("main", 'if __name__ == "__main__":\n\t${main()}', "ponto de entrada"),
  s("for", "for ${item} in ${itens}:\n\t${pass}", "for"),
  s("with", 'with open("${arquivo}", encoding="utf-8") as ${f}:\n\t${}', "abrir arquivo"),
  s("try", "try:\n\t${}\nexcept ${Exception} as e:\n\t${print(e)}", "try/except"),
  s("pr", "print(${})", "print"),
];

export const jsSnippetSource = completeFromList(jsSnippets);
export const pythonSnippetSource = completeFromList(pythonSnippets);

// ───────────── Caminhos do projeto ─────────────

/** Caminho de `to` visto a partir da pasta de `from` (com "../" quando precisa). */
export function relativePath(from: string, to: string): string {
  const a = from.split("/").slice(0, -1);
  const b = to.split("/");
  let i = 0;
  while (i < a.length && i < b.length - 1 && a[i] === b[i]) i++;
  const up = a.length - i;
  return `${up ? "../".repeat(up) : ""}${b.slice(i).join("/")}`;
}

const PATH_CONTEXTS: Array<{ re: RegExp; dotSlash: boolean }> = [
  // import x from "./a" · import("./a") · require("./a") · import "./a"
  { re: /(?:\bfrom\s+|\bimport\s*\(\s*|\brequire\s*\(\s*|\bimport\s+)(["'])([^"'\n]*)$/, dotSlash: true },
  // src="…" · href="…" (HTML/JSX)
  { re: /\b(?:src|href|action|poster)\s*=\s*(["'])([^"'\n]*)$/, dotSlash: false },
  // url(…) no CSS · @import "…"
  { re: /(?:\burl\(\s*|@import\s+)(["']?)([^"')\n]*)$/, dotSlash: false },
];

/**
 * Completa caminhos de arquivos do projeto dentro de import/require, src/href e url() — com o
 * caminho relativo ao arquivo aberto (sistema com várias páginas e módulos sem errar caminho).
 */
export function projectPathSource(currentPath: () => string, files: () => string[]): CompletionSource {
  return (ctx) => {
    const line = ctx.state.doc.lineAt(ctx.pos);
    const before = line.text.slice(0, ctx.pos - line.from);
    for (const { re, dotSlash } of PATH_CONTEXTS) {
      const m = re.exec(before);
      if (!m) continue;
      const typed = m[2] ?? "";
      if (/^(https?:|\/\/|data:|#|mailto:)/.test(typed)) return null;
      // Pacotes do npm (import "react") não são caminhos do projeto.
      if (dotSlash && typed && !typed.startsWith(".") && !typed.startsWith("/")) return null;
      const here = currentPath();
      const options: Completion[] = [];
      for (const f of files()) {
        if (f === here) continue;
        let rel = relativePath(here, f);
        if (dotSlash && !rel.startsWith("../")) rel = `./${rel}`;
        if (typed && !rel.toLowerCase().startsWith(typed.toLowerCase())) continue;
        options.push({ label: rel, type: "file", detail: "arquivo do projeto" });
        if (options.length >= 60) break;
      }
      if (!options.length) return null;
      return { from: ctx.pos - typed.length, options, validFor: /^[\w./@-]*$/ };
    }
    return null;
  };
}

// ───────────── Renomear a tag par ─────────────

const NAME_NODES = new Set([
  "TagName",
  "JSXIdentifier",
  "JSXBuiltin",
  "JSXMemberExpression",
  "JSXNamespacedName",
]);
const OPEN = new Set(["OpenTag", "JSXOpenTag"]);
const CLOSE = new Set(["CloseTag", "JSXCloseTag"]);

function tagNameOf(tag: SyntaxNode): SyntaxNode | null {
  for (let c = tag.firstChild; c; c = c.nextSibling) if (NAME_NODES.has(c.name)) return c;
  return null;
}

/** A tag do outro lado (abertura ↔ fechamento) do mesmo elemento. */
function pairedTag(tag: SyntaxNode): SyntaxNode | null {
  const element = tag.parent;
  if (!element) return null;
  if (OPEN.has(tag.name)) {
    const last = element.lastChild;
    return last && CLOSE.has(last.name) ? last : null;
  }
  if (CLOSE.has(tag.name)) {
    const first = element.firstChild;
    return first && OPEN.has(first.name) ? first : null;
  }
  return null;
}

/**
 * Editou o nome de uma tag (<div> → <section>)? A tag par muda junto, na mesma transação —
 * como o "linked editing" do VS Code, em HTML, XML, JSX e Vue.
 */
export const autoRenameTag: Extension = EditorState.transactionFilter.of((tr) => {
  if (!tr.docChanged || !(tr.isUserEvent("input") || tr.isUserEvent("delete"))) return tr;
  let changes = 0;
  let fromA = 0;
  let toA = 0;
  tr.changes.iterChangedRanges((a, b) => {
    changes++;
    fromA = a;
    toA = b;
  });
  if (changes !== 1) return tr;
  const tree = syntaxTree(tr.startState);
  // O nome que está sendo editado (o cursor pode estar logo depois dele).
  let name: SyntaxNode | null = tree.resolveInner(fromA, 1);
  if (!NAME_NODES.has(name.name)) name = tree.resolveInner(fromA, -1);
  if (!NAME_NODES.has(name.name) || fromA < name.from || toA > name.to) return tr;
  const tag = name.parent;
  if (!tag || (!OPEN.has(tag.name) && !CLOSE.has(tag.name))) return tr;
  const pair = pairedTag(tag);
  const pairName = pair && tagNameOf(pair);
  if (!pairName) return tr;
  const oldName = tr.startState.sliceDoc(name.from, name.to);
  if (tr.startState.sliceDoc(pairName.from, pairName.to) !== oldName) return tr;
  const newFrom = tr.changes.mapPos(name.from, -1);
  const newTo = tr.changes.mapPos(name.to, 1);
  const newName = tr.newDoc.sliceString(newFrom, newTo);
  if (!/^[\w.:-]*$/.test(newName) || newName === oldName) return tr;
  const spec: TransactionSpec = {
    changes: {
      from: tr.changes.mapPos(pairName.from, -1),
      to: tr.changes.mapPos(pairName.to, 1),
      insert: newName,
    },
    sequential: true,
  };
  return [tr, spec];
});

/** Palavras do próprio arquivo como sugestão (em qualquer linguagem). */
export const wordCompletion: Extension = EditorState.languageData.of(() => [
  { autocomplete: completeAnyWord },
]);
